import { useId } from 'react';

/**
 * Charts, drawn as inline SVG.
 *
 * No charting library: these are two shapes, they must take their colour from
 * the theme tokens (so they re-tint with the light/dark toggle rather than
 * being baked at build time), and every library brings a canvas or a second
 * colour system with it.
 *
 * Both are labelled for assistive technology, because a chart with no text
 * alternative is a figure a screen-reader user simply does not get.
 */

export interface AreaChartProps {
  points: number[];
  labels: string[];
  height?: number;
}

export function AreaChart({ points, labels, height = 170 }: AreaChartProps) {
  const gradientId = useId();
  if (points.length === 0) return null;

  const width = 600;
  const plotHeight = 150;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;

  const x = (i: number) => (points.length === 1 ? 0 : (i / (points.length - 1)) * width);
  const y = (v: number) => plotHeight - ((v - min) / span) * (plotHeight - 20) - 10;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p)}`).join(' ');
  const area = `${line} L${width},${plotHeight} L0,${plotHeight} Z`;

  const first = labels[0];
  const last = labels[labels.length - 1];
  const total = points.reduce((a, b) => a + b, 0);

  return (
    <>
      <svg
        viewBox={`0 0 ${width} ${plotHeight}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: `${height}px`, marginTop: 'var(--space-3)' }}
        role="img"
        aria-label={`Trend from ${first} to ${last}. ${points.length} points, peak ${Math.round(max).toLocaleString('en-PH')}, total ${Math.round(total).toLocaleString('en-PH')}.`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--portal-accent)" stopOpacity="0.3" />
            <stop offset="1" stopColor="var(--portal-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1="0"
            y1={plotHeight * f}
            x2={width}
            y2={plotHeight * f}
            stroke="var(--color-divider)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        <path d={area} fill={`url(#${gradientId})`} />
        <path
          d={line}
          fill="none"
          stroke="var(--portal-accent)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div className="chart-axis text-subtle">
        {sampleLabels(labels, 5).map((l) => (
          <span key={l}>{new Date(l).toLocaleDateString('en-PH', { day: 'numeric', month: 'short' })}</span>
        ))}
      </div>
    </>
  );
}

export function Sparkline({ points, label }: { points: number[]; label: string }) {
  if (points.length === 0) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const d = points
    .map((p, i) => {
      const x = (i / (points.length - 1 || 1)) * 100;
      const y = 24 - ((p - min) / span) * 20 - 2;
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');

  return (
    <svg
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      style={{ width: '100%', height: '24px' }}
      role="img"
      aria-label={label}
    >
      <path
        d={d}
        fill="none"
        stroke="var(--portal-accent)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Evenly spaced labels, always including the first and the last. */
function sampleLabels(labels: string[], count: number): string[] {
  if (labels.length <= count) return labels;
  const step = (labels.length - 1) / (count - 1);
  return Array.from({ length: count }, (_, i) => labels[Math.round(i * step)]!);
}

/**
 * Two series on one plot: this period against the immediately prior period of
 * equal length.
 *
 * The prior series is drawn dashed and in the muted role rather than a second
 * hue — Nocturne is a mono palette, and inventing a second accent to
 * distinguish two lines would break the system for a distinction that dash and
 * weight already carry.
 */
export function ComparisonChart({
  current,
  prior,
  labels,
  height = 170,
}: {
  current: number[];
  prior: number[];
  labels: string[];
  height?: number;
}) {
  const gradientId = useId();
  if (current.length === 0) return null;

  const width = 600;
  const plotHeight = 150;
  const all = [...current, ...prior];
  const max = Math.max(...all, 1);
  const min = Math.min(...all, 0);
  const span = max - min || 1;

  const path = (points: number[]) => {
    if (points.length === 0) return '';
    return points
      .map((v, i) => {
        const x = points.length === 1 ? 0 : (i / (points.length - 1)) * width;
        const y = plotHeight - ((v - min) / span) * (plotHeight - 20) - 10;
        return `${i === 0 ? 'M' : 'L'}${x},${y}`;
      })
      .join(' ');
  };

  const currentPath = path(current);
  const total = current.reduce((a, b) => a + b, 0);
  const priorTotal = prior.reduce((a, b) => a + b, 0);

  return (
    <>
      <svg
        viewBox={`0 0 ${width} ${plotHeight}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: `${height}px`, marginTop: 'var(--space-3)' }}
        role="img"
        aria-label={`Trend from ${labels[0]} to ${labels[labels.length - 1]}. This period totals ${Math.round(total).toLocaleString('en-PH')}, the prior period ${Math.round(priorTotal).toLocaleString('en-PH')}.`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--portal-accent)" stopOpacity="0.28" />
            <stop offset="1" stopColor="var(--portal-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1="0"
            y1={plotHeight * f}
            x2={width}
            y2={plotHeight * f}
            stroke="var(--color-divider)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {prior.length > 0 && (
          <path
            d={path(prior)}
            fill="none"
            stroke="var(--color-text-subtle)"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            vectorEffect="non-scaling-stroke"
          />
        )}

        <path d={`${currentPath} L${width},${plotHeight} L0,${plotHeight} Z`} fill={`url(#${gradientId})`} />
        <path
          d={currentPath}
          fill="none"
          stroke="var(--portal-accent)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div className="chart-axis text-subtle">
        {sampleLabels(labels, 5).map((l) => (
          <span key={l}>
            {new Date(l).toLocaleDateString('en-PH', { day: 'numeric', month: 'short' })}
          </span>
        ))}
      </div>
    </>
  );
}

/**
 * Deposits against withdrawals, per week.
 *
 * Drawn as paired bars rather than a stacked one: the question the chart
 * answers is "how much of what came in went back out", and a stack makes that
 * comparison require arithmetic.
 */
export function WeeklyBars({
  rows,
}: {
  rows: { label: string; deposits: number; withdrawals: number }[];
}) {
  if (rows.length === 0) return null;
  const max = Math.max(...rows.flatMap((r) => [r.deposits, r.withdrawals]), 1);

  return (
    <div
      className="week-bars"
      role="img"
      aria-label={rows
        .map(
          (r) =>
            `${r.label}: deposits ${Math.round(r.deposits).toLocaleString('en-PH')}, withdrawals ${Math.round(r.withdrawals).toLocaleString('en-PH')}`,
        )
        .join('. ')}
    >
      {rows.map((r) => (
        <div className="week-col" key={r.label}>
          <div className="week-pair">
            <div className="week-bar deposits" style={{ height: `${(r.deposits / max) * 100}%` }} />
            <div
              className="week-bar withdrawals"
              style={{ height: `${(r.withdrawals / max) * 100}%` }}
            />
          </div>
          <span className="week-label">{r.label}</span>
        </div>
      ))}
    </div>
  );
}
